import { ArrowRight, FolderOpen, BarChart3 } from "lucide-react";
import { SourceLoadOverviewCard } from "@/components/projects/SourceLoadCard";
import { TargetConnectionCard } from "@/components/projects/TargetConnectionCard";
import { ScardShell, Pill } from "@/components/projects/project-card-shell";
import { compactTokens, compactUsd } from "@/lib/format";
import { cn } from "@/lib/utils";
import { canReachProjectRoute } from "@/lib/project-nav";
import { useCurrentUser } from "@/hooks/useAuth";
import type { Artifact, Project } from "@/types/api";
import { useTranslation } from "react-i18next";

interface SummaryCardsProps {
  projectId: string;
  project: Project;
  artifacts: Artifact[];
  totalCost: number;
  totalTokens: number;
  modelLabel?: string | null;
  /** Rótulo do modelo de embeddings, do backend. */
  embeddingModelLabel?: string | null;
}

export function SummaryCards({
  projectId,
  project,
  artifacts,
  totalCost,
  totalTokens,
  modelLabel,
  embeddingModelLabel,
}: SummaryCardsProps) {
  const { t } = useTranslation();
  /**
   * O card só aparece para quem alcança `/projects/:id/cost` — mesmo mínimo
   * do catálogo (`lib/project-nav.ts`), não um `role` escrito à mão aqui.
   *
   * Casa canônica do custo é `/cost` (#429/SIMP.8, dado real desde então) —
   * este card navega para lá em vez de abrir a própria quebra por agente
   * numa gaveta (`CostSummaryTab`, aposentado), mesmo padrão que o card de
   * origem já usa para `/connections` desde #428. Duas vitrines de
   * custo divergindo é a duplicação que #428 fechou para conexões.
   */
  const me = useCurrentUser();
  const showCost = canReachProjectRoute(me?.role, "cost");

  return (
    <section>
      <div className="mb-4">
        <div className="font-mono text-[10px] text-[#ffc83d] tracking-[0.22em] uppercase font-semibold mb-1.5">
          {t("The Outcome")}
        </div>
        <h2 className="font-brand font-bold text-[22px] text-[#08254c] m-0 tracking-[-0.015em]">
          {t("Project resources")}
        </h2>
      </div>

      {/* Sem o card de custo sobram três: manter quatro colunas deixaria um vão
          do tamanho de um card, que lê como "faltou carregar alguma coisa". */}
      <div
        className={cn(
          "grid grid-cols-1 md:grid-cols-2 gap-5 auto-rows-fr items-stretch",
          showCost ? "xl:grid-cols-4" : "xl:grid-cols-3",
        )}
      >
        {/* Casa canônica da origem é /connections (#428): o cartão navega para lá. Desde a
            #1785 a origem é a carga do ex-kit, e não mais as conexões de origem. */}
        <SourceLoadOverviewCard project={project} />

        <TargetConnectionCard projectId={projectId} linkToConnections />

        {/* Casa canônica dos artefatos é /artifacts (#1042), como /connections e
            /cost para os outros dois cards. A gaveta que ficava aqui repetia,
            mais pobre, a lista daquela tela. Sem gate próprio: a Overview e
            Artifacts têm o mesmo mínimo no catálogo (`consultant`). */}
        <ScardShell
          to={`/projects/${projectId}/artifacts`}
          overline={t("Generated")}
          title={t("Artifacts")}
          pill={artifacts.length > 0 ? <Pill tone="gold">+{artifacts.length} total</Pill> : undefined}
          stat={
            <div className="flex items-baseline gap-2.5">
              <span className="font-brand font-extrabold text-[56px] leading-none tracking-[-0.035em] text-[#ffc83d]">
                {artifacts.length}
              </span>
              <span className="font-mono text-xs text-[#596b85] tracking-[0.08em] uppercase self-end pb-2.5">
                {t("files")}
              </span>
            </div>
          }
          meta={[]}
          cta={
            <>
              <span className="flex items-center gap-1.5">
                <FolderOpen className="w-3.5 h-3.5" /> {t("View all artifacts")}
              </span>
              <ArrowRight className="w-3.5 h-3.5" />
            </>
          }
        />

        {showCost && (
          <ScardShell
            to={`/projects/${projectId}/cost`}
            overline={t("Usage")}
            title={t("Cost & Tokens")}
            stat={
              <div className="flex items-baseline gap-2.5 min-w-0">
                {/* Abreviado acima de mil: a 56px, `$24691.34` mede ~270px
                    numa coluna de ~215px, e o `overflow-hidden` do card
                    cortava o último dígito sem nada indicar o corte. */}
                <span className="font-brand font-extrabold text-[56px] leading-none tracking-[-0.035em] text-[#ffc83d]">
                  {compactUsd(totalCost)}
                </span>
                {/* Abreviado: `toLocaleString()` punha 9 dígitos e dois
                    separadores ao lado de um número de 56px, e a linha
                    quebrava no meio do valor — `$34.13 / 8,311,` numa linha
                    e `tok.` na seguinte. O mesmo helper do hero desta
                    página (S-075 Fase 0). */}
                <span className="font-brand font-normal italic text-[22px] leading-[1.1] tracking-[-0.015em] text-[#08254c]">
                  / {compactTokens(totalTokens)} tok.
                </span>
              </div>
            }
            meta={[
              // Vêm do backend (`GET /api/projects/{id}/costs`). O split
              // input/output que ficava aqui (`totalTokens * 0.7`) era
              // fabricado — o executor nunca grava `prompt_tokens` /
              // `completion_tokens` por execução (#429/SIMP.8) — e saiu
              // sem substituto em vez de continuar inventando o número.
              { k: t("Model"), v: modelLabel || "—" },
              { k: t("Embeddings"), v: embeddingModelLabel || "—" },
            ]}
            cta={
              <>
                <span className="flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5" /> {t("View breakdown")}
                </span>
                <ArrowRight className="w-3.5 h-3.5" />
              </>
            }
          />
        )}
      </div>
    </section>
  );
}

